import { loadStripe } from '@stripe/stripe-js/pure';
import { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import StripeForm from './StripeForm';
import { getFormActions } from '../../../utils/form-helpers';
import { getGiverAddress } from '../../../payment/reward-giver-info-form-config';
import { initiateStripePayment } from '../../../payment/stripe-actions';
import { doesAnyRewardNeedAddressOrPhonenumber } from '../cart/cart-model';
import { useProject } from '../../project-hooks/useProject';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { fromKrToOre, getPathForPostPayment } from '../../../payment/payment-model';
import { useNavigate, useSearchParams } from 'react-router';
import styles from './stripe.module.less';
import { Heading4 } from '@sb1/ffe-core-react';
import { paymentMethodTitle } from '../payment-methods/paymentMethodUtils';
import { PAYMENT_METHOD_STRIPE } from '../payment-methods/PaymentMethods';
import stripeFormConfig from '../../../payment/stripe-form-config';

const stripeFormActions = getFormActions(stripeFormConfig);

const PaymentElementAppearance = {
    theme: 'flat',
    variables: {
        colorBackground: '#ffffff',
        colorPrimary: '#1f1f1f',
        colorText: '#1f1f1f',
        colorDanger: '#e44244',
        borderRadius: '8px',
        fontFamily: 'SpareBank1-regular, arial, sans-serif',
        spacingUnit: '8px',
        iconCardErrorColor: '#7c1214',
        iconCardCvcErrorColor: '#7c1214',
    },
    rules: {
        '.Input': { padding: '12px 14px', border: '1px solid #e5e7eb' },
        '.Input:focus': {
            border: '1px solid #005aa4',
            outline: '2px solid #dc8000',
            outlineOffset: '3px',
            boxShadow: '0 0 0 1px #005aa4',
        },
        '.Input:hover:focus': {
            borderColor: '#005aa4',
            boxShadow: '0 0 0 1px #005aa4',
        },
        '.Input:hover': { border: '1px solid #020a0a26', boxShadow: '0 0 0 1px #020a0a33' },
        '.Input--invalid': {
            borderColor: 'var(colorDanger)',
            backgroundColor: '#ffe3e0',
            color: '#7c1214',
        },
        '.Label': {
            color: '#1f1f1f',
            fontSize: '1rem',
            fontWeight: 'bold',
            fontFamily: 'SpareBank1-medium, arial, sans-serif',
        },
        '.Tab, .Block': { borderRadius: '12px' },
        '.Error': { fontSize: '1rem', color: '#1f1f1f' },
        '.AccordionItem': { paddingRight: '5px', paddingLeft: '5px' },
    },
};

const Stripe = forwardRef(
    (
        {
            giverName,
            comment,
            commentImageUrl,
            memorialCommentId,
            consentedToPersonalizedNewsletter,
            totalAmount,
            rewards,
            userIsUsingNorwegianLanguage,
        },
        ref
    ) => {
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const [searchParams] = useSearchParams();
        const projectPollOptionIdString = searchParams.get('projectPollOptionId');
        const projectPollOptionId = projectPollOptionIdString ? parseInt(projectPollOptionIdString, 10) : undefined;
        const project = useProject();
        const { subproject } = useSubproject();
        const stripeKey = useSelector((state) => state.stripeKey);
        const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);

        const [stripePromise, setStripePromise] = useState(null);

        useEffect(() => {
            setStripePromise(
                loadStripe(stripeKey, {
                    locale: userIsUsingNorwegianLanguage ? 'nb' : 'auto',
                })
            );
        }, [stripeKey]);

        const startStripePayment = ({ stripe, email, confirmationTokenId }) => {
            const rewardGiverInfoMetadata = doesAnyRewardNeedAddressOrPhonenumber({ rewards })
                ? getGiverAddress(rewardGiverInfoForm)
                : null;

            dispatch(
                initiateStripePayment({
                    projectId: project.id,
                    title: project.title,
                    amount: totalAmount,
                    rewards,
                    confirmationTokenId,
                    email,
                    isFinished,
                    rewardGiverInfoMetadata,
                    stripe,
                    giverName,
                    comment,
                    commentImageUrl,
                    memorialCommentId,
                    subprojectId: subproject?.id,
                    projectPollOptionId,
                    international: !userIsUsingNorwegianLanguage,
                    consentedToPersonalizedNewsletter,
                })
            );
        };

        const isFinished = ({ didGiveSupport, transactionForGiverInfo } = {}) => {
            dispatch(
                stripeFormActions.setFormFields({
                    isProcessingPayment: false,
                })
            );
            if (didGiveSupport) {
                navigate(
                    getPathForPostPayment({
                        transaction: transactionForGiverInfo,
                        projectId: project?.id,
                    })
                );
            }
        };

        const stripeOptions = {
            mode: 'payment',
            currency: 'nok',
            paymentMethodTypes: ['card'],
            amount: fromKrToOre(totalAmount),
            paymentMethodCreation: 'manual',
            appearance: PaymentElementAppearance,
        };

        return (
            <div className={styles.container}>
                <Heading4 lookLike={5}>{paymentMethodTitle(PAYMENT_METHOD_STRIPE)}</Heading4>
                <Elements stripe={stripePromise} options={stripeOptions}>
                    <StripeForm
                        ref={ref}
                        initiateCallback={startStripePayment}
                        rewards={rewards}
                        giverName={giverName}
                    />
                </Elements>
            </div>
        );
    }
);

Stripe.displayName = 'Stripe';

export default Stripe;
